import { useDispatch, useSelector } from 'react-redux';
import { selectTokenBalance } from '../../../redux/slices/walletSlice';
import CreateGiftSkeleton from './CreateGiftSkeleton';
import { openModal } from '../../../redux/slices/modals.js';
import { useCreateGiftMutation } from '../../../redux/api/authorized/gifts.js';
import WidthContainer from '../../../components/WidthContainer/index.tsx';
import GiftCardForm from '../../../components/GiftCardForm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSolana } from '../../../hooks/useSolana.js';
import { useEffect, useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import {
  EVENT_DEPOSIT_MODAL_OPENED,
  EVENT_GIFTCARD_CREATED,
  sendEvent,
  TOKEN,
} from '../../../common/analytics.js';
import useCustomLogin from '../../../hooks/useCustomLogin.js';
import { selectTokenBySymbol } from '../../../redux/slices/tokenSlice.ts';
import { compressBase64Image } from '../../../common/images.js';

const CreateGift = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toTokenAmount } = useSolana();
  const [formValues, setFormValues] = useState(null);

  const { login } = useCustomLogin();
  const { user } = usePrivy();

  useEffect(() => {
    if (!user) {
      login();
    }
  }, [user]);

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || 'VETS';
  const flow = searchParams.get('flow');

  const usdcToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'USDC'),
  );
  const toToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', token),
  );
  const usdcBalance = useSelector((state) =>
    selectTokenBalance('solana', usdcToken?.address || '')(state),
  );
  const toTokenBalance = useSelector((state) =>
    selectTokenBalance('solana', toToken?.address || '')(state),
  );

  const [createGift] = useCreateGiftMutation();

  const useCheckoutFlow =
    formValues?.baseToken === usdcToken?.address &&
    (flow === 'checkout' || usdcBalance < 1);

  const handleSubmit = async (formData) => {
    const baseTokenAmount = parseFloat(formData.baseTokenAmount);
    const isUsingUSDC = formData.baseToken === usdcToken.address;
    const payingToken = isUsingUSDC ? usdcToken : toToken;
    const tokenBalance = isUsingUSDC ? usdcBalance : toTokenBalance;

    if (useCheckoutFlow) {
      // If using checkout flow, create gift and navigate to checkout
      const compressedImage = await compressBase64Image(formData.cardImage);
      const giftResponse = await createGift({
        email: formData.email,
        token: formData.token,
        network: formData.network,
        baseToken: formData.baseToken,
        baseTokenAmount: toTokenAmount(
          formData.baseTokenAmount,
          payingToken?.decimals,
        ),
        image: compressedImage,
        message: formData.message,
      }).unwrap();
      sendEvent(EVENT_GIFTCARD_CREATED, {
        id: giftResponse?.gift?.id,
        token: formData.token,
        base_token_amount: formData.baseToken,
        base_token: formData.baseTokenAmount,
        created_date: new Date().toISOString(),
      });
      navigate(`/app/gifts/${giftResponse?.gift?.id}/checkout`);
    } else {
      // If not using checkout flow, check balance and handle accordingly
      if (tokenBalance < baseTokenAmount) {
        sendEvent(EVENT_DEPOSIT_MODAL_OPENED, {
          from: TOKEN,
        });
        dispatch(
          openModal({
            name: 'addFunds',
            props: { amount: baseTokenAmount - tokenBalance },
          }),
        );
      } else {
        const compressedImage = await compressBase64Image(formData.cardImage);
        const giftResponse = await createGift({
          email: formData.email,
          token: formData.token,
          network: formData.network,
          baseToken: formData.baseToken,
          baseTokenAmount: toTokenAmount(
            formData.baseTokenAmount,
            payingToken?.decimals,
          ),
          image: compressedImage,
          message: formData.message,
        }).unwrap();
        sendEvent(EVENT_GIFTCARD_CREATED, {
          id: giftResponse?.gift?.id,
          token: formData.token,
          base_token_amount: formData.baseToken,
          base_token: formData.baseTokenAmount,
          created_date: new Date().toISOString(),
        });
        navigate(`/app/gifts/${giftResponse?.gift?.id}/confirm`);
      }
    }
  };

  if (!usdcToken || !toToken) {
    return <CreateGiftSkeleton />;
  }

  return (
    <WidthContainer>
      <GiftCardForm
        initialValues={{
          token: toToken.address,
          network: toToken.network,
          baseToken: usdcToken.address,
        }}
        onSubmit={handleSubmit}
        backButtonPath={`/app/tokens/${toToken.address}`}
        checkoutFlow={useCheckoutFlow}
        onFormValuesChange={setFormValues}
      />
    </WidthContainer>
  );
};

export default CreateGift;
